import React, { useState, useMemo } from 'react';
import {
  FlaskConical, Plus, CheckCircle, XCircle, Info,
  AlertTriangle, Users, BookOpen, Check, Search, X,
  ChevronDown, ChevronRight as ChevronRightIcon,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useGetOptionsQuery, useGetPolesQuery } from '../../services/polesOptionsApi';
import {
  useCreerTPMultiOptionsMutation,
  useGetMatieresQuery,
} from '../../services/matieresApi';

// ─── Composant principal ────────────────────────────────────────────────────

const GestionTP: React.FC = () => {
  // Formulaire TP
  const [nomTP, setNomTP] = useState('');
  const [credits, setCredits] = useState(2.0);
  const [coefDevoir, setCoefDevoir] = useState(0.40);
  const [coefExamen, setCoefExamen] = useState(0.60);
  const [niveau, setNiveau] = useState('L1');
  const [semestre, setSemestre] = useState(1);

  // Sélection multi-options
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [filtrePole, setFiltrePole] = useState<string>('');

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: options = [] } = useGetOptionsQuery({ actif: true });
  const { data: poles = [] } = useGetPolesQuery({});
  const [creerTP, { isLoading }] = useCreerTPMultiOptionsMutation();

  // Liste des TPs déjà créés pour ce niveau/semestre
  const { data: matieresExistantes = [] } = useGetMatieresQuery({ niveau, semestre });

  const tpsExistants = useMemo(() => {
    return matieresExistantes.filter(m =>
      m.nom_matiere.toLowerCase().startsWith('tp') ||
      m.nom_matiere.toLowerCase().includes('travaux pratiques')
    );
  }, [matieresExistantes]);

  // Options filtrées par pôle
  const optionsFiltrees = useMemo(() => {
    if (!filtrePole) return options;
    return options.filter(o => o.pole_nom === filtrePole);
  }, [options, filtrePole]);

  // Toggle option
  const toggleOption = (id: number) => {
    setSelectedOptions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllFiltered = () => {
    setSelectedOptions(optionsFiltrees.map(o => o.id));
  };

  const clearSelection = () => setSelectedOptions([]);

  // Validation coefficients
  const coefSomme = Math.round((coefDevoir + coefExamen) * 100) / 100;
  const coefValide = Math.abs(coefSomme - 1.0) < 0.01;

  const handleCreer = async () => {
    setMessage(null);

    if (!nomTP.trim()) {
      setMessage({ type: 'error', text: 'Donnez un nom au TP.' });
      return;
    }
    if (selectedOptions.length === 0) {
      setMessage({ type: 'error', text: 'Sélectionnez au moins une option.' });
      return;
    }
    if (!coefValide) {
      setMessage({ type: 'error', text: `Coef devoir + examen doit = 1.0 (actuel : ${coefSomme})` });
      return;
    }

    try {
      const result = await creerTP({
        nom_tp: nomTP.trim(),
        credits,
        coefficient_devoir: coefDevoir,
        coefficient_examen: coefExamen,
        niveau,
        semestre,
        option_ids: selectedOptions,
      }).unwrap();

      setMessage({ type: 'success', text: result.message });
      setNomTP('');
      setSelectedOptions([]);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.error || 'Erreur création TP' });
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <FlaskConical size={28} className="text-orange-600" />
          Gestion des Travaux Pratiques (TP)
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
          Créez un TP et affectez-le simultanément à plusieurs options dans leur UE "Travaux Pratiques".
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg border flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-800 dark:text-red-300'
        }`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <XCircle size={16} />}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {/* Formulaire TP */}
      <Card>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
          <Plus size={16} /> Créer un nouveau TP
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nom du TP *</label>
            <input
              type="text"
              value={nomTP}
              onChange={e => setNomTP(e.target.value)}
              placeholder="Ex: Électronique I, Mécanique des Fluides, Programmation C..."
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500">Le préfixe "TP - " sera ajouté automatiquement si absent.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Crédits</label>
            <input
              type="number"
              step={0.5}
              min={0.5}
              max={10}
              value={credits}
              onChange={e => setCredits(parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Coef. Devoir</label>
            <input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={coefDevoir}
              onChange={e => setCoefDevoir(parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Coef. Examen</label>
            <input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={coefExamen}
              onChange={e => setCoefExamen(parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="flex items-center">
            <div className={`px-3 py-1.5 rounded text-xs font-medium ${
              coefValide
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
            }`}>
              Total coef : {coefSomme} {coefValide ? '✓' : '✗ (doit = 1.0)'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Niveau *</label>
            <select
              value={niveau}
              onChange={e => setNiveau(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {['L1', 'L2', 'L3', 'M1', 'M2'].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Semestre *</label>
            <select
              value={semestre}
              onChange={e => setSemestre(Number(e.target.value))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {[1, 2, 3, 4, 5, 6].map(s => <option key={s} value={s}>S{s}</option>)}
            </select>
          </div>
        </div>
      </Card>

      {/* Sélection des options */}
      <Card>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Users size={16} /> Options cibles ({selectedOptions.length} / {optionsFiltrees.length} sélectionnée{selectedOptions.length > 1 ? 's' : ''})
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={filtrePole}
              onChange={e => setFiltrePole(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous les pôles</option>
              {poles.map(p => <option key={p.id} value={p.nom}>{p.nom}</option>)}
            </select>
            <button
              onClick={selectAllFiltered}
              className="px-2 py-1.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200"
            >
              Tout sélectionner
            </button>
            <button
              onClick={clearSelection}
              className="px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-200"
            >
              Vider
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {optionsFiltrees.map(o => {
            const isSelected = selectedOptions.includes(o.id);
            return (
              <button
                key={o.id}
                onClick={() => toggleOption(o.id)}
                className={`text-left px-3 py-2 rounded-lg border transition-colors ${
                  isSelected
                    ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-400 text-orange-800 dark:text-orange-200'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1 rounded">{o.code}</span>
                      <span className="text-[10px] text-gray-400">{o.pole_nom?.substring(0, 4)}</span>
                    </div>
                    <div className="text-xs font-medium mt-0.5 truncate" title={o.nom}>{o.nom}</div>
                  </div>
                  {isSelected && <Check size={14} className="text-orange-600 flex-shrink-0 ml-1" />}
                </div>
              </button>
            );
          })}
        </div>

        {optionsFiltrees.length === 0 && (
          <div className="py-6 text-center text-sm text-gray-400">
            Aucune option pour ce pôle.
          </div>
        )}
      </Card>

      {/* Aperçu */}
      {nomTP.trim() && selectedOptions.length > 0 && (
        <Card className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-700">
          <h3 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-2">
            <Info size={16} /> Aperçu
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Le TP <strong>"{nomTP.toLowerCase().startsWith('tp') ? nomTP : `TP - ${nomTP}`}"</strong>
            {' '}sera créé avec <strong>{credits} crédits</strong> dans l'UE "Travaux Pratiques" de{' '}
            <strong>{selectedOptions.length} option(s)</strong> en <strong>{niveau} S{semestre}</strong>.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Si l'UE "Travaux Pratiques" n'existe pas encore pour une option, elle sera créée automatiquement.
          </p>
        </Card>
      )}

      {/* Bouton action */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          icon={<Plus size={16} />}
          onClick={handleCreer}
          disabled={isLoading || !nomTP.trim() || selectedOptions.length === 0 || !coefValide}
        >
          {isLoading ? 'Création...' : `Créer le TP dans ${selectedOptions.length} option(s)`}
        </Button>
      </div>

      {/* TPs existants */}
      <TPsExistantsTable
        tpsExistants={tpsExistants}
        niveau={niveau}
        semestre={semestre}
      />


      {/* Conseil */}
      <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300 flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <strong>Conseil :</strong> si plusieurs options partagent le même TP (ex : "TP Électronique" pour L1MI, L1BTP, L1MT),
          sélectionnez-les toutes et créez le TP en une seule fois. Cela garantit cohérence et évite la duplication manuelle.
        </div>
      </div>
    </div>
  );
};

// ─── Composant : Liste des TPs existants avec recherche + tri/groupage ──────

type GroupBy = 'aucun' | 'option' | 'classe';

interface TPsExistantsTableProps {
  tpsExistants: any[];
  niveau: string;
  semestre: number;
}

const TPsExistantsTable: React.FC<TPsExistantsTableProps> = ({ tpsExistants, niveau, semestre }) => {
  const [recherche, setRecherche] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>('option');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Filtrage par recherche (nom, code, option, classe)
  const tpsFiltres = useMemo(() => {
    if (!recherche.trim()) return tpsExistants;
    const q = recherche.trim().toLowerCase();
    return tpsExistants.filter(m =>
      (m.nom_matiere || '').toLowerCase().includes(q) ||
      (m.code_matiere || '').toLowerCase().includes(q) ||
      (m.option || '').toLowerCase().includes(q) ||
      (m.classe || '').toLowerCase().includes(q) ||
      (m.ue_nom || '').toLowerCase().includes(q)
    );
  }, [tpsExistants, recherche]);

  // Tri / groupage
  const groupes = useMemo(() => {
    if (groupBy === 'aucun') {
      return [{ key: '__all__', label: '', items: [...tpsFiltres].sort((a, b) => (a.nom_matiere || '').localeCompare(b.nom_matiere || '', 'fr')) }];
    }
    const map = new Map<string, any[]>();
    for (const m of tpsFiltres) {
      const cle = (groupBy === 'option' ? m.option : m.classe) || '— Non assigné —';
      if (!map.has(cle)) map.set(cle, []);
      map.get(cle)!.push(m);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b, 'fr'))
      .map(([key, items]) => ({
        key,
        label: key,
        items: items.sort((a: any, b: any) => (a.nom_matiere || '').localeCompare(b.nom_matiere || '', 'fr')),
      }));
  }, [tpsFiltres, groupBy]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const collapseAll = () => setCollapsedGroups(new Set(groupes.map(g => g.key)));
  const expandAll = () => setCollapsedGroups(new Set());

  return (
    <Card>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <BookOpen size={16} /> TPs existants pour {niveau} S{semestre}
          <span className="text-xs font-normal text-gray-400">
            ({tpsFiltres.length}{recherche && ` / ${tpsExistants.length}`})
          </span>
        </h3>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Recherche */}
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={recherche}
              onChange={e => setRecherche(e.target.value)}
              placeholder="Filtrer par nom, code, option…"
              className="pl-7 pr-7 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-56 focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
            {recherche && (
              <button onClick={() => setRecherche('')} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                <X size={12} />
              </button>
            )}
          </div>

          {/* Groupage */}
          <div className="flex items-center gap-1 text-xs">
            <span className="text-gray-500">Grouper :</span>
            {(['aucun', 'option', 'classe'] as GroupBy[]).map(g => (
              <button
                key={g}
                onClick={() => setGroupBy(g)}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  groupBy === g
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
                }`}
              >
                {g === 'aucun' ? 'Aucun' : g === 'option' ? 'Par option' : 'Par classe'}
              </button>
            ))}
          </div>

          {groupBy !== 'aucun' && groupes.length > 1 && (
            <div className="flex items-center gap-1 text-xs">
              <button onClick={expandAll} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 rounded hover:bg-gray-200">
                Tout ouvrir
              </button>
              <button onClick={collapseAll} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 rounded hover:bg-gray-200">
                Tout fermer
              </button>
            </div>
          )}
        </div>
      </div>

      {tpsFiltres.length === 0 ? (
        <div className="py-6 text-center text-sm text-gray-400">
          <FlaskConical size={32} className="mx-auto mb-2 opacity-40" />
          {recherche ? `Aucun TP ne correspond à « ${recherche} »` : 'Aucun TP pour l\'instant'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          {groupes.map(groupe => {
            const collapsed = collapsedGroups.has(groupe.key);
            const showHeader = groupBy !== 'aucun';
            return (
              <div key={groupe.key} className={showHeader ? 'mb-3 last:mb-0' : ''}>
                {showHeader && (
                  <button
                    onClick={() => toggleGroup(groupe.key)}
                    className="w-full flex items-center justify-between px-3 py-1.5 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-t text-left hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {collapsed ? <ChevronRightIcon size={14} className="text-orange-600" /> : <ChevronDown size={14} className="text-orange-600" />}
                      <span className="text-sm font-semibold text-orange-800 dark:text-orange-200">{groupe.label}</span>
                      <span className="text-xs text-orange-600/70 dark:text-orange-400/70">
                        ({groupe.items.length} TP{groupe.items.length > 1 ? 's' : ''})
                      </span>
                    </div>
                  </button>
                )}
                {!collapsed && (
                  <table className={`w-full text-sm ${showHeader ? 'border-x border-b border-orange-200 dark:border-orange-700 rounded-b' : ''}`}>
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 text-xs">Code</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 text-xs">Nom TP</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 text-xs">UE</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 text-xs">Option</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 text-xs">Classe</th>
                        <th className="text-center px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 text-xs">Crédits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupe.items.map((m: any) => (
                        <tr key={m.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{m.code_matiere}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{m.nom_matiere}</td>
                          <td className="px-3 py-2 text-xs text-gray-500 truncate max-w-[200px]" title={m.ue_nom || '—'}>{m.ue_nom || '—'}</td>
                          <td className="px-3 py-2 text-xs">
                            {m.option ? (
                              <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium">{m.option}</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {m.classe ? (
                              <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-mono font-bold">{m.classe}</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center text-sm">{m.credits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default GestionTP;
